import { IsNumber, IsString } from "@pdfking/tfunc"

import type { Point } from "../types/geometry/Point"
import type { Rectangle } from "../types/geometry/Rectangle"
import type { SpatialRectangle } from "../types/geometry/SpatialRectangle"
import type { Scale } from "../types/scale/Scale"
import { ScaleEnum } from "../types/scale/ScaleEnum"
import { GetBleed } from "./GetBleed"

/**
 * Information needed to compute scale values
 */
type ScaleInfo = {
  inner: Rectangle
  media: SpatialRectangle
  crop: SpatialRectangle
}

/**
 * Helper function to create a Point with the same value for x and y
 */
const createPoint = (value: number): Point => ({
  x: value,
  y: value,
})

/**
 * Calculates sketch scaling based on media and inner dimensions
 */
function sketchScale(
  { inner, media, crop }: ScaleInfo,
  use_bleed: boolean = false
): Point {
  const bleed = use_bleed ? GetBleed(media, crop) : 0

  return {
    x: (2 * bleed + media.width) / inner.width,
    y: (2 * bleed + media.height) / inner.height,
  }
}

/**
 * Scales content to be contained within the target area
 */
function containScale(info: ScaleInfo, use_bleed: boolean = false): Point {
  const { x, y } = sketchScale(info, use_bleed)
  return createPoint(Math.min(x, y))
}

/**
 * Scales content to fill the target area
 */
function fillScale(info: ScaleInfo, use_bleed: boolean = false): Point {
  const { x, y } = sketchScale(info, use_bleed)
  return createPoint(Math.max(x, y))
}

/**
 * Converts a scale value into x and y scaling factors
 */
export const ComputeScale = (info: ScaleInfo, scale: Scale): Point => {
  // Handle numeric case (same scale for both dimensions)
  if (IsNumber(scale)) {
    return createPoint(scale)
  }

  // Handle enum case or string format
  if (IsString(scale)) {
    // Handle enum values directly
    switch (scale) {
      case ScaleEnum.FULL:
        return createPoint(1)

      case ScaleEnum.CONTAIN:
        return containScale(info)

      case ScaleEnum.FILL:
        return fillScale(info)

      case ScaleEnum.SKETCH:
        return sketchScale(info)

      case ScaleEnum.CONTAIN_BLEED:
        return containScale(info, true)

      case ScaleEnum.FILL_BLEED:
        return fillScale(info, true)

      case ScaleEnum.SKETCH_BLEED:
        return sketchScale(info, true)
    }

    if (scale.startsWith("scale-")) {
      const s = parseInt(scale.split("-")[1]) / 100
      return createPoint(s)
    }

    throw new Error(`Unsupported scale type: ${scale}`)
  }

  return scale || createPoint(1)
}
